import { useDisclosure } from '@nextui-org/react'
import { useMutation } from '@tanstack/react-query'
import { FileText, Trash2, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { JavaIcon, MavenIcon, MuleIcon } from '~/components/common/RuntimeIcons'
import { SettingsControlItem, type DropdownOption } from '~/components/common/SettingsControlItem'
import { SettingsSection } from '~/components/common/SettingsSection'
import { DefaultVersion, JavaVersion, MavenVersion, MuleRuntimeVersion } from '~/constants/settings'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { readFileAsText } from '~/services/onboardingService'
import { changeXmlSettings } from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'
import SettingSecurityRequestModal from '~/components/modals/SettingSecurityRequestModal'

const mavenOptions: DropdownOption[] = Object.values(MavenVersion).map(v => ({
  key: v,
  label: `Maven ${v}`
}))

const javaOptions: DropdownOption[] = Object.values(JavaVersion).map(v => ({
  key: v,
  label: `Java ${v}`
}))

const muleOptions: DropdownOption[] = Object.values(MuleRuntimeVersion).map(v => ({
  key: v,
  label: `Mule ${v}`
}))

export function XmlSettingsSection() {
  const { isOpen: isUploadMoreModalOpen, onOpenChange: onUploadMoreModalOpenChange } =
    useDisclosure()

  const { currentWorkspace, updateCurrentWorkspaceRuntimeSetting, refetch } = useWorkspaceContext()
  const isAdmin = currentWorkspace?.is_admin ?? false

  const settingsXmlRawTxt = currentWorkspace?.org_runtime_setting?.settings_xml_raw_txt || ''
  const settingsSecurityXmlRawTxt =
    currentWorkspace?.org_runtime_setting?.settings_security_xml_raw_txt || ''

  const hasSettingsXml = settingsXmlRawTxt.length > 0
  const hasSecurityXml = settingsSecurityXmlRawTxt.length > 0
  const hasAnyXml = hasSettingsXml || hasSecurityXml

  const [mavenVersion, setMavenVersion] = useState<MavenVersion>(
    DefaultVersion.mavenVersion as unknown as MavenVersion
  )
  const [javaVersion, setJavaVersion] = useState<JavaVersion>(
    DefaultVersion.javaVersion as unknown as JavaVersion
  )
  const [muleVersion, setMuleVersion] = useState<MuleRuntimeVersion>(
    DefaultVersion.muleRuntimeVersion as unknown as MuleRuntimeVersion
  )

  // Rehydrate the selectors from the persisted org runtime setting on load and
  // whenever the workspace switches. Always assign (falling back to the default)
  // so switching to a workspace without a saved version resets the selector
  // instead of showing the previous workspace's value.
  const savedMavenVersion = currentWorkspace?.org_runtime_setting?.maven_version
  const savedJavaVersion = currentWorkspace?.org_runtime_setting?.java_version
  const savedMuleVersion = currentWorkspace?.org_runtime_setting?.mule_runtime_version
  useEffect(() => {
    setMavenVersion(
      (savedMavenVersion as MavenVersion) ||
        (DefaultVersion.mavenVersion as unknown as MavenVersion)
    )
    setJavaVersion(
      (savedJavaVersion as JavaVersion) || (DefaultVersion.javaVersion as unknown as JavaVersion)
    )
    setMuleVersion(
      (savedMuleVersion as MuleRuntimeVersion) ||
        (DefaultVersion.muleRuntimeVersion as unknown as MuleRuntimeVersion)
    )
  }, [savedMavenVersion, savedJavaVersion, savedMuleVersion])

  const settingsFileName = currentWorkspace?.org_runtime_setting?.settings_xml_file_name || null
  const securitySettingsFileName =
    currentWorkspace?.org_runtime_setting?.settings_security_xml_file_name || null
  const [latestSettingsXmlContent, setLatestSettingsXmlContent] = useState<string | null>(null)

  const { mutateAsync: updateXmlSettings } = useMutation<
    unknown,
    unknown,
    {
      java_version?: string
      maven_version?: string
      mule_runtime_version?: string
      settings_xml_raw_txt?: string
      settings_security_xml_raw_txt?: string
      settings_xml_file_name?: string
      settings_security_xml_file_name?: string
    }
  >({
    mutationFn: xmlSettings => {
      if (!currentWorkspace) {
        throw new Error('Workspace not found')
      }
      return changeXmlSettings(currentWorkspace.uuid, xmlSettings)
    },
    onSuccess: (_data, variables) => {
      if (variables) {
        updateCurrentWorkspaceRuntimeSetting({
          org_runtime_setting: {
            ...currentWorkspace?.org_runtime_setting,
            ...variables
          }
        })
      }
      refetch()
    }
  })

  const handleUploadSettingsXML = (isSettingsSecurityXML = false) => {
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = '.xml'

    fileInput.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        try {
          const xmlContent = await readFileAsText(file)

          if (isSettingsSecurityXML) {
            const currentSettingsFileName =
              currentWorkspace?.org_runtime_setting?.settings_xml_file_name || ''
            const newXmlSettings = {
              settings_xml_raw_txt: latestSettingsXmlContent ?? settingsXmlRawTxt,
              settings_security_xml_raw_txt: xmlContent,
              settings_xml_file_name: currentSettingsFileName,
              settings_security_xml_file_name: file.name
            }

            try {
              await updateXmlSettings(newXmlSettings)
              BetterToast.success('Settings Security XML uploaded successfully')
            } catch (error) {
              BetterToast.error('Failed to save Settings Security XML')
              console.error('Error saving settings security XML:', error)
            }
          } else {
            const currentSecurityFileName =
              currentWorkspace?.org_runtime_setting?.settings_security_xml_file_name || ''
            const newXmlSettings = {
              settings_xml_raw_txt: xmlContent,
              settings_security_xml_raw_txt: settingsSecurityXmlRawTxt,
              settings_xml_file_name: file.name,
              settings_security_xml_file_name: currentSecurityFileName
            }

            try {
              await updateXmlSettings(newXmlSettings)
              setLatestSettingsXmlContent(xmlContent)
              BetterToast.success('Settings XML uploaded successfully')
              if (!hasSecurityXml) {
                onUploadMoreModalOpenChange()
              }
            } catch (error) {
              BetterToast.error('Failed to save Settings XML')
              console.error('Error saving settings XML:', error)
            }
          }
        } catch (error) {
          BetterToast.error('Failed to read Settings XML file')
          console.error('Error reading settings XML:', error)
        }
      }
    }

    fileInput.click()
  }

  const handleRemoveSettingsXml = async () => {
    try {
      await updateXmlSettings({
        settings_xml_raw_txt: '',
        settings_security_xml_raw_txt: settingsSecurityXmlRawTxt,
        settings_xml_file_name: '',
        settings_security_xml_file_name:
          currentWorkspace?.org_runtime_setting?.settings_security_xml_file_name || ''
      })
      setLatestSettingsXmlContent(null)
      BetterToast.success('Settings XML removed')
    } catch (error) {
      BetterToast.error('Failed to remove Settings XML')
      console.error('Error removing settings XML:', error)
    }
  }

  const handleRemoveSecurityXml = async () => {
    try {
      await updateXmlSettings({
        settings_xml_raw_txt: settingsXmlRawTxt,
        settings_security_xml_raw_txt: '',
        settings_xml_file_name: currentWorkspace?.org_runtime_setting?.settings_xml_file_name || '',
        settings_security_xml_file_name: ''
      })
      BetterToast.success('Settings Security XML removed')
    } catch (error) {
      BetterToast.error('Failed to remove Settings Security XML')
      console.error('Error removing settings security XML:', error)
    }
  }

  const handleConfirmUpload = () => {
    if (!isUploadMoreModalOpen || (!latestSettingsXmlContent && !hasSettingsXml)) {
      return
    }
    handleUploadSettingsXML(true)
  }

  const handleCancelUpload = () => {
    setLatestSettingsXmlContent(null)
    onUploadMoreModalOpenChange()
  }

  const handleMavenChange = async (key: string) => {
    const previous = mavenVersion
    setMavenVersion(key as MavenVersion)
    try {
      await updateXmlSettings({ maven_version: key })
      BetterToast.success(`Maven version set to ${key}`)
    } catch (error) {
      setMavenVersion(previous)
      BetterToast.error('Failed to save Maven version')
      console.error('Error saving Maven version:', error)
    }
  }

  const handleJavaChange = async (key: string) => {
    const previous = javaVersion
    setJavaVersion(key as JavaVersion)
    try {
      await updateXmlSettings({ java_version: key })
      BetterToast.success(`Java version set to ${key}`)
    } catch (error) {
      setJavaVersion(previous)
      BetterToast.error('Failed to save Java version')
      console.error('Error saving Java version:', error)
    }
  }

  const handleMuleChange = async (key: string) => {
    const previous = muleVersion
    setMuleVersion(key as MuleRuntimeVersion)
    try {
      await updateXmlSettings({ mule_runtime_version: key })
      BetterToast.success(`Mule version set to ${key}`)
    } catch (error) {
      setMuleVersion(previous)
      BetterToast.error('Failed to save Mule version')
      console.error('Error saving Mule version:', error)
    }
  }

  return (
    <>
      <SettingsSection>
        <SettingsControlItem
          variant='selection'
          icon={<MavenIcon />}
          label='Maven Version'
          value={`Maven ${mavenVersion}`}
          options={mavenOptions}
          onSelect={isAdmin ? handleMavenChange : undefined}
        />
        <SettingsControlItem
          variant='selection'
          icon={<JavaIcon />}
          label='Java Version'
          value={`Java ${javaVersion}`}
          options={javaOptions}
          onSelect={isAdmin ? handleJavaChange : undefined}
        />
        <SettingsControlItem
          variant='selection'
          icon={<MuleIcon />}
          label='Mule Version'
          value={`Mule ${muleVersion}`}
          options={muleOptions}
          onSelect={isAdmin ? handleMuleChange : undefined}
          hideBorder
        />

        {/* Dashed divider */}
        <div className='w-full border-t border-dashed border-tokens-neutral-border-2' />

        {/* Settings XML — always first: show entry if exists, upload button if missing */}
        {hasSettingsXml ? (
          <div className='group flex items-center gap-4 py-4'>
            <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-primary-2'>
              <FileText className='w-[18px] h-[18px]' />
            </span>
            <div className='flex flex-col items-start gap-0.5 px-2'>
              <span className='font-mono font-semibold text-[12px] leading-normal text-tokens-primary-2'>
                Settings XML
              </span>
              <span className='font-sans text-[12px] leading-[18px] text-tokens-neutral-text-secondary'>
                {settingsFileName || 'settings.xml'}
              </span>
            </div>
            {isAdmin && (
              <button
                type='button'
                onClick={handleRemoveSettingsXml}
                className='flex items-center justify-center w-6 h-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-tokens-status-error-text text-tokens-neutral-icon-solid'
                aria-label='Remove settings.xml'
              >
                <Trash2 className='w-[18px] h-[18px]' />
              </button>
            )}
          </div>
        ) : isAdmin ? (
          <div
            role='button'
            tabIndex={0}
            onClick={() => handleUploadSettingsXML()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleUploadSettingsXML()
              }
            }}
            className='group flex items-center gap-4 w-full py-4 cursor-pointer'
          >
            <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
              <Upload className='w-[18px] h-[18px]' />
            </span>
            <div className='flex flex-col items-start gap-0.5 px-2'>
              <span className='font-sans font-medium text-[14px] leading-[20px] tracking-[0.1px] text-[rgba(0,0,0,0.88)] group-hover:text-tokens-status-link-3 transition-colors'>
                Upload settings.xml
              </span>
              <span className='font-sans text-[12px] leading-[22px] text-[rgba(0,0,0,0.45)]'>
                XML file with maximum size of 5 MB
              </span>
            </div>
          </div>
        ) : null}

        {/* Security Settings XML — always second: show entry if exists, upload button if missing */}
        {hasSecurityXml ? (
          <div className='group flex items-center gap-4 py-4'>
            <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-primary-2'>
              <FileText className='w-[18px] h-[18px]' />
            </span>
            <div className='flex flex-col items-start gap-0.5 px-2'>
              <span className='font-mono font-semibold text-[12px] leading-normal text-tokens-primary-2'>
                Security Settings XML
              </span>
              <span className='font-sans text-[12px] leading-[18px] text-tokens-neutral-text-secondary'>
                {securitySettingsFileName || 'settings-security.xml'}
              </span>
            </div>
            {isAdmin && (
              <button
                type='button'
                onClick={handleRemoveSecurityXml}
                className='flex items-center justify-center w-6 h-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:text-tokens-status-error-text text-tokens-neutral-icon-solid'
                aria-label='Remove settings_security.xml'
              >
                <Trash2 className='w-[18px] h-[18px]' />
              </button>
            )}
          </div>
        ) : hasSettingsXml && isAdmin ? (
          <div
            role='button'
            tabIndex={0}
            onClick={() => handleUploadSettingsXML(true)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleUploadSettingsXML(true)
              }
            }}
            className='group flex items-center gap-4 w-full py-4 cursor-pointer'
          >
            <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
              <Upload className='w-[18px] h-[18px]' />
            </span>
            <div className='flex flex-col items-start gap-0.5 px-2'>
              <span className='font-sans font-medium text-[14px] leading-[20px] tracking-[0.1px] text-[rgba(0,0,0,0.88)] group-hover:text-tokens-status-link-3 transition-colors'>
                Upload settings-security.xml
              </span>
              <span className='font-sans text-[12px] leading-[22px] text-[rgba(0,0,0,0.45)]'>
                XML file with maximum size of 5 MB
              </span>
            </div>
          </div>
        ) : null}
      </SettingsSection>

      <SettingSecurityRequestModal
        isOpen={isUploadMoreModalOpen}
        onOpenChange={onUploadMoreModalOpenChange}
        onConfirm={handleConfirmUpload}
        onCancel={handleCancelUpload}
      />
    </>
  )
}
